import React, { createContext, useContext } from 'react';
import { Variables } from '../modules/end-user/create-your-assistant/create-your-assistant.props';

// Is user logged in
type userLoggedInType = {
  isLoggedIn: boolean;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
};

const initialUserLoggedInContext: userLoggedInType = {
  isLoggedIn: false,
  setIsLoggedIn: () => {},
};

export const LoginContext = createContext<userLoggedInType>(initialUserLoggedInContext);

// Creating a user context to be globally accessible
type userObjectType = {
  email?: string;
  first_name?: string;
  last_name?: string;
};

type userType = {
  user: userObjectType;
  setUser: (user: userObjectType) => void;
};

const initialUserContext: userType = {
  user: {},
  setUser: () => {},
};

export const UserContext = createContext<userType>(initialUserContext);

// Creating a context to change the heading in the Header on component
type headingType = {
  heading: string;
  setHeading: (heading: string) => void;
};

const initialHeadingContext: headingType = {
  heading: '',
  setHeading: () => {},
};

export const HeadingContext = createContext<headingType>(initialHeadingContext);

type modalType = {
  open: boolean;
  setOpen: (args: boolean) => void;
};

const initialModalOpenContext: modalType = {
  open: false,
  setOpen: () => {},
};

export const ModalContext = createContext<modalType>(initialModalOpenContext);

// modal open for budget
type budgetType = {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
};

const initialBudgetContext: budgetType = {
  isModalOpen: false,
  setIsModalOpen: () => {},
};

export const BudgetContext = createContext<budgetType>(initialBudgetContext);

type headerType = {
  userName: string;
  setUserName: (userName: string) => void;
};

const initialHeaderContext: headerType = {
  userName: '',
  setUserName: () => {},
};

export const HeaderContext = createContext<headerType>(initialHeaderContext);

// Nav sidebar collapsed state (so content can adjust width when nav is collapsed)
type NavCollapsedType = {
  isNavCollapsed: boolean;
  setIsNavCollapsed: (value: boolean) => void;
};
const initialNavCollapsed: NavCollapsedType = {
  isNavCollapsed: false,
  setIsNavCollapsed: () => {},
};
export const NavCollapsedContext = createContext<NavCollapsedType>(initialNavCollapsed);

/** Whether the “add provider / LLM” setup banner should show (driven from stored permissions). */
type LlmSetupBannerContextType = {
  showLlmSetupBanner: boolean;
  setShowLlmSetupBanner: (value: boolean) => void;
};

const initialLlmSetupBannerContext: LlmSetupBannerContextType = {
  showLlmSetupBanner: false,
  setShowLlmSetupBanner: () => {},
};

export const LlmSetupBannerContext = createContext<LlmSetupBannerContextType>(
  initialLlmSetupBannerContext,
);

// active model for chat
type chatModelType = {
  chatModel: string;
  setChatModel: (chatModel: string) => void;
};

const initialChatModelContext: chatModelType = {
  chatModel: '',
  setChatModel: () => {},
};

export const ChatModelContext = createContext<chatModelType>(initialChatModelContext);

export interface HeaderTokenObject {
  total_budget: number;
  remaining_budget: number;
}
// active model for chat
type HeaderTokenType = {
  HeaderTokens: HeaderTokenObject;
  setHeaderTokens: (args: HeaderTokenObject) => void;
};

// Initial context values
const initialHeaderTokenContext: HeaderTokenType = {
  HeaderTokens: { total_budget: 0, remaining_budget: 0 },
  setHeaderTokens: () => {},
};

export const HeaderTokenContext = createContext<HeaderTokenType>(initialHeaderTokenContext);

// save app in assistant
type saveModalType = {
  saveAppModal: boolean;
  setSaveAppModal: (saveAppModal: boolean) => void;
};

const initialSaveAppContext: saveModalType = {
  saveAppModal: false,
  setSaveAppModal: () => {},
};

export const SaveAppModalContext = createContext<saveModalType>(initialSaveAppContext);

export type identifiers = {
  pii_config_enabled: boolean;
  default_identifiers?: number[];
  global?: number[];
  usa?: number[];
  india?: number[];
};

// Save app in assistant
export type piiType = {
  piiIdentifiers: identifiers;
  setPiiIdentifiers: React.Dispatch<React.SetStateAction<identifiers>>;
};

// Initialize default context
const initialPiiContext: piiType = {
  piiIdentifiers: {
    default_identifiers: [],
    global: [],
    usa: [],
    india: [],
    pii_config_enabled: false,
  },
  setPiiIdentifiers: () => {},
};

export const PiiContext = createContext<piiType>(initialPiiContext);

type promptData = {
  model_id: number;
  max_tokens: number;
  temperature: string;
};

export type ModelContextType = {
  promptData: promptData;
  setPromptData: (args: promptData) => void;
};

const initialModelIdContext: ModelContextType = {
  promptData: { model_id: 0, max_tokens: 0, temperature: '' },
  setPromptData: () => {},
};

export const ModelIdContext = createContext<ModelContextType>(initialModelIdContext);

export type WorkflowSelectModal = {
  workflowSelectModalOpen: boolean;
  setWorkflowSelectModalOpen: (workflowSelectModalOpen: boolean) => void;
};

const initialWorkflowSelectModalContext: WorkflowSelectModal = {
  workflowSelectModalOpen: false,
  setWorkflowSelectModalOpen: () => {},
};

export const WorkflowSelectModalContext = createContext<WorkflowSelectModal>(
  initialWorkflowSelectModalContext,
);

export type triggerConfigData = {
  interval_range?: number;
  day_of_month_dropdown?: number;
  time_options?: string;
  timezone_dropdown?: string;
  day_selection?: string;
  toggle_weekends?: boolean;
  child_key?: string;
  [key: string]: string | number | boolean | undefined | object;
};

export type actionConfig = {
  [key: string]: string | number | boolean | undefined | object;
};

export type workFlowDataType = {
  workflow_id?: number | string;
  trigger_catalogue_id?: string;
  trigger_config?: triggerConfigData;
  new_project?: boolean;
};

export type WorkFlowContextType = {
  workFlowData: workFlowDataType;
  setWorkFlowData: React.Dispatch<React.SetStateAction<workFlowDataType>>;
};

export const initialWorkFlowContext: WorkFlowContextType = {
  workFlowData: {
    workflow_id: '',
    trigger_catalogue_id: '',
    trigger_config: {
      interval_range: 0,
      day_of_month_dropdown: 0,
      time_options: '',
      timezone_dropdown: '',
      day_selection: '',
      toggle_weekends: false,
      child_key: '',
    },
  },
  setWorkFlowData: () => {},
};

export const WorkFlowContext = createContext<WorkFlowContextType>(initialWorkFlowContext);

export type NestedRecord = {
  [key: string]: string | number | boolean | NestedRecord;
};

export type workFlowActionDataType = {
  action_order?: string | number;
  workflow_id?: string;
  project_id?: string;
  action_id?: string;
  previous_id?: string;
  prev_execution_id?: string;
  action_catalogue_id?: string;
  action_config?: actionConfig;
  isSetUpTabCompleted?: boolean;
  isConfigTabCompleted?: boolean;
  isTestTabCompleted?: boolean;
};

export type WorkFlowContextActionType = {
  workFlowActionData: workFlowActionDataType;
  setWorkFlowActionData: React.Dispatch<React.SetStateAction<workFlowActionDataType>>;
};

const initialWorkFlowActionContext: WorkFlowContextActionType = {
  workFlowActionData: {
    action_order: 0,
    workflow_id: '',
    action_catalogue_id: '',
    project_id: '',
    action_id: '',
    previous_id: '',
    prev_execution_id: '',
    action_config: {},
    isSetUpTabCompleted: false,
    isConfigTabCompleted: false,
    isTestTabCompleted: false,
  },
  setWorkFlowActionData: () => {},
};

export const WorkFlowActionContext = createContext<WorkFlowContextActionType>(
  initialWorkFlowActionContext,
);

export type DisableWorkflowFields = {
  disableMode: boolean;
  setDisableMode: (disableMode: boolean) => void;
};

const initialWorkflowDisableContext: DisableWorkflowFields = {
  disableMode: false,
  setDisableMode: () => {},
};

export const DisableWorkflowContext = createContext<DisableWorkflowFields>(
  initialWorkflowDisableContext,
);

export type FileContextType = {
  uploadedFile: Variables[] | null;
  setUploadedFile: React.Dispatch<React.SetStateAction<Variables[] | null>>;
};

export const FileContext = createContext<FileContextType | null>(null);

export const useFileContext = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFileContext must be used within a FileProvider');
  }
  return context;
};
